import { useState } from 'react'
import { ChevronDown, GripVertical, Plus, Star, X } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ImageUpload from '@/components/ImageUpload'
import { createSponsorClientId } from '@/lib/sponsors'
import type {
  CommitteeMember,
  Entertainment as EntertainmentType,
  Host,
  Honoree,
  Sponsor,
} from '@/types'

function SortableSponsorCard({
  id,
  sponsor,
  index,
  onUpdate,
  onRemove,
}: {
  id: string
  sponsor: Sponsor
  index: number
  onUpdate: (index: number, field: keyof Sponsor, value: string | boolean | null) => void
  onRemove: (index: number) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="relative space-y-3 rounded-lg border border-border/50 p-3">
      <div className="absolute top-2 right-2 flex items-center gap-0.5">
        <button
          type="button"
          className="cursor-grab rounded-md p-1 text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="rounded-md p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex gap-3 pr-14">
        <div className="w-24 shrink-0">
          <Label className="text-sm">Logo</Label>
          <ImageUpload
            currentUrl={sponsor.logo_url ?? null}
            onUpload={(signedId) => onUpdate(index, 'logo', signedId || null)}
            onPreviewChange={(url) => onUpdate(index, 'logo_url', url)}
            className="mt-1"
          />
        </div>
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-sm">Company Name</Label>
              <Input
                value={sponsor.name}
                onChange={(event) => onUpdate(index, 'name', event.target.value)}
                placeholder="Acme Corp"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Sponsorship Level</Label>
              <Input
                value={sponsor.tier}
                onChange={(event) => onUpdate(index, 'tier', event.target.value)}
                placeholder="e.g. Gold, Platinum, Community Partner"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-sm">Website URL</Label>
              <Input
                value={sponsor.url ?? ''}
                onChange={(event) => onUpdate(index, 'url', event.target.value || null)}
                placeholder="https://example.com"
              />
            </div>
            <div className="flex items-end pb-1">
              <button
                type="button"
                onClick={() => onUpdate(index, 'featured', !sponsor.featured)}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
                  sponsor.featured
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Star className={`size-3.5 ${sponsor.featured ? 'fill-primary' : ''}`} />
                Featured
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type EventRichFields = {
  hosts: Host[]
  honorees: Honoree[]
  entertainment: EntertainmentType[]
  sponsors: Sponsor[]
  committee_members: CommitteeMember[]
  ticket_link: string
  auction_url: string
  donation_url: string
  raffle_url: string
}

type EventFormRichSectionsProps = {
  value: EventRichFields
  onChange: (value: EventRichFields | ((current: EventRichFields) => EventRichFields)) => void
  pro: boolean
  sections?: RichSectionKey[]
}

export type RichSectionKey = 'hosts' | 'honorees' | 'entertainment' | 'sponsors' | 'event_links' | 'committee'

export default function EventFormRichSections({
  value,
  onChange,
  pro,
  sections,
}: EventFormRichSectionsProps) {
  const [openSections, setOpenSections] = useState({
    hosts: value.hosts.length > 0,
    honorees: value.honorees.length > 0,
    entertainment: value.entertainment.length > 0,
    sponsors: value.sponsors.length > 0,
    committee: value.committee_members.length > 0,
    event_links: !!(value.auction_url || value.donation_url || value.raffle_url),
  })

  function toggleSection(section: keyof typeof openSections) {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  function updateField<Key extends keyof EventRichFields>(
    key: Key,
    update: (current: EventRichFields[Key]) => EventRichFields[Key],
  ) {
    onChange((current) => ({ ...current, [key]: update(current[key]) }))
  }

  function addHost() {
    updateField('hosts', (hosts) => [
      ...hosts,
      { name: '', organization: '' },
    ])
  }

  function updateHost(index: number, field: keyof Host, nextValue: string) {
    updateField('hosts', (hosts) =>
      hosts.map((host, currentIndex) =>
        currentIndex === index ? { ...host, [field]: nextValue } : host,
      ),
    )
  }

  function removeHost(index: number) {
    updateField('hosts', (hosts) =>
      hosts.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  function addHonoree() {
    updateField('honorees', (honorees) => [
      ...honorees,
      { name: '', company: '', award: '' },
    ])
  }

  function updateHonoree(index: number, field: keyof Honoree, nextValue: string) {
    updateField('honorees', (honorees) =>
      honorees.map((honoree, currentIndex) =>
        currentIndex === index ? { ...honoree, [field]: nextValue } : honoree,
      ),
    )
  }

  function removeHonoree(index: number) {
    updateField('honorees', (honorees) =>
      honorees.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  function addEntertainment() {
    updateField('entertainment', (entertainment) => [
      ...entertainment,
      { name: '', type: 'band' },
    ])
  }

  function updateEntertainment(
    index: number,
    field: keyof EntertainmentType,
    nextValue: string,
  ) {
    updateField('entertainment', (entertainment) =>
      entertainment.map((item, currentIndex) =>
        currentIndex === index ? { ...item, [field]: nextValue } : item,
      ),
    )
  }

  function removeEntertainment(index: number) {
    updateField('entertainment', (entertainment) =>
      entertainment.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  function addSponsor() {
    updateField('sponsors', (sponsors) => [
      ...sponsors,
      { name: '', tier: '', url: null, featured: false, logo: null, client_id: createSponsorClientId() },
    ])
  }

  function updateSponsor(
    index: number,
    field: keyof Sponsor,
    nextValue: string | boolean | null,
  ) {
    updateField('sponsors', (sponsors) =>
      sponsors.map((sponsor, currentIndex) =>
        currentIndex === index ? { ...sponsor, [field]: nextValue } : sponsor,
      ),
    )
  }

  function removeSponsor(index: number) {
    updateField('sponsors', (sponsors) =>
      sponsors.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  function handleSponsorDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    updateField('sponsors', (sponsors) => {
      const activeId = String(active.id)
      const overId = String(over.id)
      const oldIndex = sponsors.findIndex((sponsor) => sponsor.client_id === activeId)
      const newIndex = sponsors.findIndex((sponsor) => sponsor.client_id === overId)

      if (oldIndex < 0 || newIndex < 0) return sponsors
      return arrayMove(sponsors, oldIndex, newIndex)
    })
  }

  const sponsorSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const visibleSections = sections ?? ['hosts', 'honorees', 'entertainment', 'sponsors', 'event_links', 'committee']
  const showSection = (section: RichSectionKey) => visibleSections.includes(section)
  const sectionTabsMode = !!sections
  const isSectionOpen = (section: keyof typeof openSections) => sectionTabsMode || openSections[section]

  function addCommitteeMember() {
    updateField('committee_members', (committeeMembers) => [
      ...committeeMembers,
      { name: '', role: '' },
    ])
  }

  function updateCommitteeMember(
    index: number,
    field: keyof CommitteeMember,
    nextValue: string,
  ) {
    updateField('committee_members', (committeeMembers) =>
      committeeMembers.map((member, currentIndex) =>
        currentIndex === index ? { ...member, [field]: nextValue } : member,
      ),
    )
  }

  function removeCommitteeMember(index: number) {
    updateField('committee_members', (committeeMembers) =>
      committeeMembers.filter(
        (_, currentIndex) => currentIndex !== index,
      ),
    )
  }

  return (
    <>
      {showSection('hosts') && (
        <Card>
        <CardHeader
          className={`select-none ${pro ? 'cursor-pointer' : ''}`}
          onClick={() => pro && toggleSection('hosts')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Hosts</CardTitle>
              {!pro && (
                <Badge variant="outline" className="text-sm">Pro</Badge>
              )}
              {pro && value.hosts.length > 0 && (
                <Badge variant="secondary">{value.hosts.length}</Badge>
              )}
            </div>
            {pro && (
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  isSectionOpen('hosts') ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </CardHeader>
        {!pro && (
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-base text-muted-foreground">
                Add hosts to highlight who is leading your event.
              </p>
              <Button asChild variant="outline" size="sm" className="ml-4 shrink-0">
                <a href="/dashboard/billing">Upgrade</a>
              </Button>
            </div>
          </CardContent>
        )}
        {pro && isSectionOpen('hosts') && (
          <CardContent className="space-y-4">
            {value.hosts.map((host, index) => (
              <div
                key={index}
                className="relative rounded-lg border border-border/50 p-3"
              >
                <button
                  type="button"
                  onClick={() => removeHost(index)}
                  className="absolute top-2 right-2 rounded-md p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
                <div className="grid grid-cols-2 gap-3 pr-8">
                  <div className="space-y-1">
                    <Label className="text-sm">Name</Label>
                    <Input
                      value={host.name}
                      onChange={(event) =>
                        updateHost(index, 'name', event.target.value)
                      }
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Organization</Label>
                    <Input
                      value={host.organization}
                      onChange={(event) =>
                        updateHost(index, 'organization', event.target.value)
                      }
                      placeholder="Nuestros Ninos"
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addHost}
              className="gap-1.5"
            >
              <Plus className="size-4" /> Add Host
            </Button>
          </CardContent>
        )}
        </Card>
      )}

      {showSection('honorees') && (
        <Card>
        <CardHeader
          className={`select-none ${pro ? 'cursor-pointer' : ''}`}
          onClick={() => pro && toggleSection('honorees')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Honorees</CardTitle>
              {!pro && (
                <Badge variant="outline" className="text-sm">Pro</Badge>
              )}
              {pro && value.honorees.length > 0 && (
                <Badge variant="secondary">{value.honorees.length}</Badge>
              )}
            </div>
            {pro && (
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  isSectionOpen('honorees') ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </CardHeader>
        {!pro && (
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-base text-muted-foreground">
                Add honorees to showcase the people being recognized at your event.
              </p>
              <Button asChild variant="outline" size="sm" className="ml-4 shrink-0">
                <a href="/dashboard/billing">Upgrade</a>
              </Button>
            </div>
          </CardContent>
        )}
        {pro && isSectionOpen('honorees') && (
          <CardContent className="space-y-4">
            {value.honorees.map((honoree, index) => (
              <div
                key={index}
                className="relative space-y-2 rounded-lg border border-border/50 p-3"
              >
                <button
                  type="button"
                  onClick={() => removeHonoree(index)}
                  className="absolute top-2 right-2 rounded-md p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
                <div className="grid grid-cols-2 gap-3 pr-8">
                  <div className="space-y-1">
                    <Label className="text-sm">Name</Label>
                    <Input
                      value={honoree.name}
                      onChange={(event) =>
                        updateHonoree(index, 'name', event.target.value)
                      }
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Company/Affiliation</Label>
                    <Input
                      value={honoree.company}
                      onChange={(event) =>
                        updateHonoree(index, 'company', event.target.value)
                      }
                      placeholder="Acme Foundation"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Award/Recognition</Label>
                  <Input
                    value={honoree.award}
                    onChange={(event) =>
                      updateHonoree(index, 'award', event.target.value)
                    }
                    placeholder="Lifetime Achievement Award"
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addHonoree}
              className="gap-1.5"
            >
              <Plus className="size-4" /> Add Honoree
            </Button>
          </CardContent>
        )}
        </Card>
      )}

      {showSection('entertainment') && (
        <Card>
        <CardHeader
          className={`select-none ${pro ? 'cursor-pointer' : ''}`}
          onClick={() => pro && toggleSection('entertainment')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Entertainment</CardTitle>
              {!pro && (
                <Badge variant="outline" className="text-sm">Pro</Badge>
              )}
              {pro && value.entertainment.length > 0 && (
                <Badge variant="secondary">{value.entertainment.length}</Badge>
              )}
            </div>
            {pro && (
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  isSectionOpen('entertainment') ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </CardHeader>
        {!pro && (
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-base text-muted-foreground">
                Add entertainment to highlight the performers and acts at your event.
              </p>
              <Button asChild variant="outline" size="sm" className="ml-4 shrink-0">
                <a href="/dashboard/billing">Upgrade</a>
              </Button>
            </div>
          </CardContent>
        )}
        {pro && isSectionOpen('entertainment') && (
          <CardContent className="space-y-4">
            {value.entertainment.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Input
                  className="flex-1"
                  value={item.name}
                  onChange={(event) =>
                    updateEntertainment(index, 'name', event.target.value)
                  }
                  placeholder="Name"
                />
                <Select
                  value={item.type}
                  onValueChange={(nextValue) =>
                    updateEntertainment(index, 'type', nextValue)
                  }
                >
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="band">Band</SelectItem>
                      <SelectItem value="dj">DJ</SelectItem>
                      <SelectItem value="speaker">Speaker</SelectItem>
                      <SelectItem value="comedian">Comedian</SelectItem>
                      <SelectItem value="performer">Performer</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => removeEntertainment(index)}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEntertainment}
              className="gap-1.5"
            >
              <Plus className="size-4" /> Add Entertainment
            </Button>
          </CardContent>
        )}
        </Card>
      )}

      {showSection('sponsors') && (
        <Card>
        <CardHeader
          className={`select-none ${pro ? 'cursor-pointer' : ''}`}
          onClick={() => pro && toggleSection('sponsors')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Sponsors</CardTitle>
              {!pro && (
                <Badge variant="outline" className="text-sm">Pro</Badge>
              )}
              {pro && value.sponsors.length > 0 && (
                <Badge variant="secondary">{value.sponsors.length}</Badge>
              )}
            </div>
            {pro && (
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  isSectionOpen('sponsors') ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </CardHeader>
        {!pro && (
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-base text-muted-foreground">
                Add sponsors with logos to showcase the companies supporting your event.
              </p>
              <Button asChild variant="outline" size="sm" className="ml-4 shrink-0">
                <a href="/dashboard/billing">Upgrade</a>
              </Button>
            </div>
          </CardContent>
        )}
        {pro && isSectionOpen('sponsors') && (
          <CardContent className="space-y-4">
            <DndContext
              sensors={sponsorSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSponsorDragEnd}
            >
              <SortableContext
                items={value.sponsors.map((sponsor, index) => sponsor.client_id ?? `sponsor-${index}`)}
                strategy={verticalListSortingStrategy}
              >
                {value.sponsors.map((sponsor, index) => (
                  <SortableSponsorCard
                    key={sponsor.client_id ?? `sponsor-${index}`}
                    id={sponsor.client_id ?? `sponsor-${index}`}
                    sponsor={sponsor}
                    index={index}
                    onUpdate={updateSponsor}
                    onRemove={removeSponsor}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSponsor}
              className="gap-1.5"
            >
              <Plus className="size-4" /> Add Sponsor
            </Button>
          </CardContent>
        )}
        </Card>
      )}

      {showSection('event_links') && (
        <Card>
        <CardHeader
          className={`select-none ${pro ? 'cursor-pointer' : ''}`}
          onClick={() => pro && toggleSection('event_links')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Additional links</CardTitle>
              {!pro && (
                <Badge variant="outline" className="text-sm">Pro</Badge>
              )}
              {pro && (value.auction_url || value.donation_url || value.raffle_url) && (
                <Badge variant="secondary">
                  {[value.auction_url, value.donation_url, value.raffle_url].filter(Boolean).length}
                </Badge>
              )}
            </div>
            {pro && (
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${
                  isSectionOpen('event_links') ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </CardHeader>
        {!pro && (
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-base text-muted-foreground">
                Add auction, donation, and raffle links to your event page.
              </p>
              <Button asChild variant="outline" size="sm" className="ml-4 shrink-0">
                <a href="/dashboard/billing">Upgrade</a>
              </Button>
            </div>
          </CardContent>
        )}
        {pro && isSectionOpen('event_links') && (
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label className="text-sm">Auction link</Label>
              <Input
                type="url"
                value={value.auction_url}
                onChange={(e) =>
                  onChange((current) => ({ ...current, auction_url: e.target.value }))
                }
                placeholder="https://auction.example.com"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Donation link</Label>
              <Input
                type="url"
                value={value.donation_url}
                onChange={(e) =>
                  onChange((current) => ({ ...current, donation_url: e.target.value }))
                }
                placeholder="https://donate.example.com"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Raffle link</Label>
              <Input
                type="url"
                value={value.raffle_url}
                onChange={(e) =>
                  onChange((current) => ({ ...current, raffle_url: e.target.value }))
                }
                placeholder="https://raffle.example.com"
              />
            </div>
          </CardContent>
        )}
        </Card>
      )}

      {showSection('committee') && (
        <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => toggleSection('committee')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Committee</CardTitle>
              {value.committee_members.length > 0 && (
                <Badge variant="secondary">
                  {value.committee_members.length}
                </Badge>
              )}
            </div>
            <ChevronDown
              className={`size-4 text-muted-foreground transition-transform ${
                isSectionOpen('committee') ? 'rotate-180' : ''
              }`}
            />
          </div>
        </CardHeader>
        {isSectionOpen('committee') && (
          <CardContent className="space-y-4">
            {value.committee_members.map((member, index) => (
              <div key={index} className="flex items-center gap-3">
                <Input
                  className="flex-1"
                  value={member.name}
                  onChange={(event) =>
                    updateCommitteeMember(index, 'name', event.target.value)
                  }
                  placeholder="Name"
                />
                <Input
                  className="flex-1"
                  value={member.role}
                  onChange={(event) =>
                    updateCommitteeMember(index, 'role', event.target.value)
                  }
                  placeholder="Role (e.g. Chair)"
                />
                <button
                  type="button"
                  onClick={() => removeCommitteeMember(index)}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCommitteeMember}
              className="gap-1.5"
            >
              <Plus className="size-4" /> Add Committee Member
            </Button>
          </CardContent>
        )}
        </Card>
      )}
    </>
  )
}
